/* oxlint-disable @typescript-eslint/no-explicit-any */
import AccuracyService from "@app/projects/network-assessment/accuracy/accuracyService";
import { Unauthorized } from "@app/shared/ErrorPages";
import { ChangeEvent, JSX, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import AccuracyEvidenceTab from "@app/tools/accuracy-evidence/AccuracyEvidenceTab";
import AccuracyEvidenceQdpTab from "@app/tools/accuracy-evidence/AccuracyEvidenceQdpTab";
import AccuracyEvidenceProviderClaimsActivityTab from "@app/tools/accuracy-evidence/AccuracyEvidenceProviderClaimsActivityTab";
import AccuracyEvidenceLocationConfidenceScoresTab from "@app/tools/accuracy-evidence/AccuracyEvidenceLocationConfidenceScoresTab";
import { EvidenceSearchOption } from "@app/tools/accuracy-evidence/enumerations";
import { SearchAddress } from "@app/tools/accuracy-evidence/models/searchAddress";
import AddressSearchBar from "@app/tools/accuracy-evidence/AddressSearchBar";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import ErrorBoundary from "@app/shared/ErrorBoundary";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESRadioGroup } from "@app/shared/qes-standards/forms/RadioGroup";
import { QESDateTimeInput } from "@app/shared/qes-standards/forms/DateTimeInput";
import { UserContext } from "@app/user/UserContext";
import { Collapse } from "bootstrap";

const computeNpiEnabled = (npi: string) => (npi && (npiEnabled(npi)));
const computeXidEnabled = (xid: string) => (xid && (xidEnabled(xid)));
const computeAddressEnabled = (address: SearchAddress) => (address && (addressEnable(address)));
const npiEnabled = (npi: string) => (npi && npi.length > 0);
const xidEnabled = (xid: string) => (xid && xid.length > 0);
const addressEnable = (address: SearchAddress) =>
(address && address.address1.length > 0
  && address.city.length > 0
  && address.zip.length > 0
  && address.state.length > 0);
const accuracyService = new AccuracyService();
const defaultDepth = 2;

export default function AccuracyEvidence(props: RouteComponentProps): JSX.Element {
  let isQdpTabExpanded = false;
  const [npi, setNpi] = useState<string>("");
  const [xid, setXid] = useState<string>("");
  const [address, setAddress] = useState<SearchAddress>({
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: ""
  });
  const [asOfDate, setAsOfDate] = useState<Date | null>(null);
  const [evidenceTabular, setEvidenceTabular] = useState<any>({});
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [searchEnabled, setSearchEnabled] = useState<boolean>(false);
  const [collapsedDepth, setCollapsedDepth] = useState<Record<string, number | boolean>>({}); //drives json expand/collapse depth.
  const [expanding, setExpanding] = useState<boolean>(false);                           //used to prevent "no data found" message from displaying when expanding/collapsing json
  const [isCollapsed, setIsCollapsed] = useState<Record<string, boolean>>({});                        //drives text on expand/collapse button
  const [selectedEvidenceType, setSelectedEvidenceType] = useState<string>("");
  const [searchOption, setSearchOption] = useState<EvidenceSearchOption>(EvidenceSearchOption.Npi);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDateOutOfRangeWarning, setShowDateOutOfRangeWarning] = useState<boolean>(false);

  const breadcrumbs = breadcrumbTemplates[BreadcrumbTemplateEnum.AccuracyEvidence];

  function handleSearchCriteriaChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;

    switch (e.target.name) {
      case "npi":
        if (value.length > 0 && (!value.match("[0-9]")) || value.length > 10) {
          return;
        }
        setNpi(value);
        break;
      case "xid":
        setXid(value);
        break;
      case "address1":
        address.address1 = value;
        setAddress(address => ({
          ...address,
        }));
        break;
      case "address2":
        address.address2 = value;
        setAddress(address => ({
          ...address,
        }));
        break;
      case "city":
        address.city = value;
        setAddress(address => ({
          ...address,
        }));
        break;
      case "zip":
        if (value.length > 0 && !value.match("[0-9]") || value.length > 5) {
          return;
        }
        address.zip = value;
        setAddress(address => ({
          ...address,
        }));
        break;
    }
  }

  function handleSearchCriteriaChangeSelect(e: ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value;

    address.state = value;
    setAddress(address => ({
      ...address,
    }));
  }

  useEffect(() => {
    const newEnabled = (computeNpiEnabled(npi) && searchOption === EvidenceSearchOption.Npi)
      || (computeXidEnabled(xid) && searchOption === EvidenceSearchOption.Xid)
      || (computeAddressEnabled(address) && searchOption === EvidenceSearchOption.Address);
    if (newEnabled !== searchEnabled) {
      setSearchEnabled(newEnabled);
    }
  });

  useEffect(() => {
    (async () => {
      const urlParams = new URLSearchParams(props.location.search);
      const npiNumber = urlParams.get("npi");
      const xid = urlParams.get("xid");
      const address1 = urlParams.get("address1");
      const address2 = urlParams.get("address2");
      const city = urlParams.get("city");
      const state = urlParams.get("state");
      const zip = urlParams.get("zip");
      const date = urlParams.get("temporalPosition");

      let dateOutOfRange = false;
      if (date) {
        const parsedDate = new Date(date);
        const cutoff = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000);
        if (parsedDate < cutoff) {
          setShowDateOutOfRangeWarning(true);
          dateOutOfRange = true;
        } else {
          setAsOfDate(parsedDate);
        }
      }

      if (isEmpty(npiNumber) && isEmpty(xid) && isEmptyAddress(address1, city, state, zip) || dateOutOfRange)
        setIsLoading(false);
      else
        setIsLoading(true);

      if (!isEmpty(npiNumber)) {
        setNpi(npiNumber);
        setSearchOption(EvidenceSearchOption.Npi);
      } else if (!isEmpty(xid)) {
        setXid(xid);
        setSearchOption(EvidenceSearchOption.Xid);
      } else if (!isEmptyAddress(address1, city, state, zip)) {
        setAddress({
          address1: address1,
          address2: address2,
          city: city,
          state: state,
          zip: zip
        });
        setSearchOption(EvidenceSearchOption.Address);
      }

    })();

  }, []);

  useEffect(() => {
    if (isLoading)
      getDocuments();
  }, [isLoading]);

  function formatDateTimeLocal(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  function formatUrlDate(date: Date): string {
    if (!date)
      return '';
    let result = new Date();
    const offset = date.getTimezoneOffset()
    result = new Date(date.getTime() - (offset * 60 * 1000))
    return result.toISOString().replace("Z", "");
  }
  function handleSubmit() {
    setIsLoading(true);
  }

  async function getDocuments() {
    if (asOfDate && asOfDate < new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)) {
      setShowDateOutOfRangeWarning(true);
      setAsOfDate(null);
      setIsLoading(false);
      return;
    }

    let docs;
    switch (searchOption) {
      case EvidenceSearchOption.Npi:
        setXid("");
        setAddress({ address1: "", address2: "", city: "", state: "", zip: "" });
        docs = await getTruthDocuments(npi, asOfDate);
        history.replaceState({}, '', `${location.pathname}?npi=${npi}${asOfDate ? `&temporalPosition=${formatUrlDate(asOfDate)}` : ""}`);
        break;
      case EvidenceSearchOption.Xid:
        setNpi("");
        setAddress({ address1: "", address2: "", city: "", state: "", zip: "" });
        docs = await getQdpEvidenceByXid(xid, asOfDate);
        history.replaceState({}, '', `${location.pathname}?xid=${xid}${asOfDate ? `&temporalPosition=${formatUrlDate(asOfDate)}` : ""}`);
        break;
      case EvidenceSearchOption.Address:
        setNpi("");
        setXid("");
        docs = await getQdpEvidenceByAddress(address, asOfDate);
        history.replaceState({}, '', `${location.pathname}?address1=${address.address1}${address.address2 ? "&address2=" + address.address2 : ""}&city=${address.city}&state=${address.state}&zip=${address.zip}${asOfDate ? `&temporalPosition=${formatUrlDate(asOfDate)}` : ""}`);
        break;
    }

    const isCollapsedTemp: { [evidenceType: string]: boolean; } = {};
    const collapsedDepthTemp: { [evidenceType: string]: boolean | number } = {}
    Object.keys(docs).forEach(evidenceType => isCollapsedTemp[evidenceType] = true);
    Object.keys(docs).forEach(evidenceType => collapsedDepthTemp[evidenceType] = defaultDepth);

    setCollapsedDepth(collapsedDepthTemp);
    setIsCollapsed(isCollapsedTemp);

    if (docs) {
      if (!isEmpty(xid)) {
        setSelectedEvidenceType("QDP");
      } else {
        setSelectedEvidenceType(Object.keys(docs)[0]);
      }
    }
  }

  async function handleClear() {
    history.replaceState({}, '', `${location.pathname}`);
    setHasSearched(false);
    setEvidenceTabular({});
    setNpi("");
    setXid("");
    setAddress({
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: ""
    });
    setAsOfDate(null);
    setSelectedEvidenceType("");
  }

  async function expandNonQdpTabs() {
    //react-json-view keeps track of whether each json node is expanded or collapsed.
    //the only way I've been able to expand/collapse all nodes is by removing the json
    //and re-adding it. Just changing the collapseDepth won't collapse/expand nodes the user has touched
    isCollapsed[selectedEvidenceType] = !isCollapsed[selectedEvidenceType];
    collapsedDepth[selectedEvidenceType] = !collapsedDepth[selectedEvidenceType];
    setIsCollapsed(isCollapsed);
    setCollapsedDepth(collapsedDepth);
    setExpanding(true);
    if (searchOption === EvidenceSearchOption.Npi)
      await getTruthDocuments(npi, asOfDate);
    else if (searchOption === EvidenceSearchOption.Xid)
      await getQdpEvidenceByXid(xid, asOfDate);
    else if (searchOption === EvidenceSearchOption.Address)
      await getQdpEvidenceByAddress(address, asOfDate);
  }

  async function getTruthDocuments(npiNumber: string, date: Date) {
    // unpack the response into state
    const response = await accuracyService.getEvidenceByNpiTabular(npiNumber, date);
    if (!isEmpty(response)) {
      setEvidenceTabular(response);
    } else {
      setEvidenceTabular({});
    }
    evidenceLoaded();
    return response;
  }

  async function getQdpEvidenceByXid(xid: string, date: Date) {
    const response = await accuracyService.getEvidenceByXidTabular(xid, date);
    if (!isEmpty(response)) {
      setEvidenceTabular(response);
    } else {
      setEvidenceTabular({});
    }
    evidenceLoaded();
    return response;
  }

  async function getQdpEvidenceByAddress(address: SearchAddress, date: Date) {
    const response = await accuracyService.getEvidenceByAddressTabular(address, date);
    if (!isEmpty(response)) {
      setEvidenceTabular(response);
    } else {
      setEvidenceTabular({});
    }
    evidenceLoaded();
    return response;
  }

  async function handleKeyUp(e: React.KeyboardEvent) {
    const value = e.key;
    if ((searchOption === EvidenceSearchOption.Npi && value === "Enter" && computeNpiEnabled(npi)) ||
      (searchOption === EvidenceSearchOption.Xid && value === "Enter" && computeXidEnabled(xid)) ||
      (searchOption === EvidenceSearchOption.Address && value === "Enter" && computeAddressEnabled(address))) {
      setIsLoading(true);
    }
  }

  function isEmpty(obj: unknown) {
    if (obj === null || obj === undefined)
      return true;

    return Object.keys(obj).length === 0;
  }

  function isEmptyAddress(address1: string, city: string, state: string, zip: string) {
    return isEmpty(address1) || isEmpty(city) || isEmpty(state) || isEmpty(zip);
  }

  function handleSearchOptionChange(selectedSearchOption: EvidenceSearchOption) {
    setSearchOption(selectedSearchOption);
  }

  function evidenceLoaded() {
    setHasSearched(true);
    setExpanding(false);
    setIsLoading(false);
  }

  function showNonQdpExpandButton() {
    return (!isEmpty(evidenceTabular)) &&
      (<div className="row justify-content-end">
        <div className="col-sm-12" >
          <QESButton
            qesTestId="accuracy-evidence_expand"
            qesType="secondary"
            className="float-end"
            onClick={expandNonQdpTabs}
          >{isCollapsed[selectedEvidenceType] ? "Expand" : "Collapse"} All
          </QESButton>
        </div>
      </div>);
  }

  function showQdpExpandButton() {
    return (!isEmpty(evidenceTabular)) &&
      (<div className="row justify-content-end">
        <div className="col-sm-12" >
          <QESButton
            qesTestId="accuracy-evidence_expand"
            qesType="secondary"
            className="float-end"
            onClick={handleQdpExpandButtonClick}
          >{isQdpTabExpanded ? "Collapse" : "Expand"} All
          </QESButton>
        </div>
      </div>);
  }

  function handleQdpExpandButtonClick(e: React.MouseEvent<HTMLElement>) {
    const collapses = document.querySelectorAll(".collapse");
    if (isQdpTabExpanded) {
      collapses.forEach((c) => new Collapse(c, { toggle: false }).hide());
    }
    else {
      collapses.forEach((c) => new Collapse(c, { toggle: false }).show());
    }

    isQdpTabExpanded = !isQdpTabExpanded;
    const target = e.target as HTMLElement;
    target.innerText = `${isQdpTabExpanded ? "Collapse" : "Expand"} All`;
  }

  function getEvidenceTypeDisplayName(evidenceType: string) {
    switch (evidenceType) {
      case "ProviderClaimsActivity":
        return "PCA";
      case "LocationConfidenceScores":
        return "ML";
      default:
        return evidenceType;
    }
  }

  function getEvidenceTab() {
    switch (selectedEvidenceType) {
      case "QDP":
        return (
          <AccuracyEvidenceQdpTab
            src={evidenceTabular[selectedEvidenceType] ?? null}
            srcList={!!evidenceTabular && evidenceTabular[selectedEvidenceType] != null && "QdpTruthSources" in evidenceTabular[selectedEvidenceType] ? evidenceTabular[selectedEvidenceType] : null}
            expanding={expanding} />
        );
      case "ProviderClaimsActivity":
        return (
          <AccuracyEvidenceProviderClaimsActivityTab
            src={evidenceTabular[selectedEvidenceType] ?? null}
            expanding={expanding} />
        );
      case "LocationConfidenceScores":
        return (
          <AccuracyEvidenceLocationConfidenceScoresTab
            src={evidenceTabular[selectedEvidenceType] ?? null}
            srcList={!!evidenceTabular && evidenceTabular[selectedEvidenceType] != null && "LocationConfidenceScoreRecords" in evidenceTabular[selectedEvidenceType] ? evidenceTabular[selectedEvidenceType] : null}
            expanding={expanding} />
        );
      default:
        return (
          <AccuracyEvidenceTab
            src={evidenceTabular[selectedEvidenceType] ?? null}
            collapsed={collapsedDepth[selectedEvidenceType]}
            expanding={expanding} />
        );
    }
  }

  return (
    <UserContext.Consumer>
      {(user) => (
        user
          ? (
            <ErrorBoundary user={user}>
              <Page documentTitle="Accuracy Evidence">
                <Breadcrumbs items={breadcrumbs} />
                <PageInner>
                  <PageHeader title="Accuracy Evidence" disableHR={false} iconName={QesIcons.SearchSm} />
                  <PageSection>
                    <div className="row mt-3">
                      <div className="col-xl-12 col-lg-10">
                        <div className="card card-expansion-item card-static">
                          <div className="card-body">
                            <div className="row mb-2">
                              <div className="col-xl-7">Accuracy Evidence
                                <div className="mt-2 ms-2">
                                  <QESRadioGroup
                                    renderInline={true}
                                    groupContainerClass="mb-1"
                                  >
                                    <QESRadioButton
                                      label="NPI"
                                      className="ms-2"
                                      checked={searchOption === EvidenceSearchOption.Npi}
                                      onChange={(e) => handleSearchOptionChange(e.target.value as EvidenceSearchOption)}
                                      value="NPI"
                                      testId="accuracy-evidence__search-criteria--npi"
                                    />
                                    <QESRadioButton
                                      label="Xid"
                                      checked={searchOption === EvidenceSearchOption.Xid}
                                      onChange={(e) => handleSearchOptionChange(e.target.value as EvidenceSearchOption)}
                                      id={`accuracy-evidence-viewer__search-option-radio-xid`}
                                      value="Xid"
                                      testId="accuracy-evidence__search-criteria--xid"
                                    />
                                    <QESRadioButton
                                      label="Address"
                                      checked={searchOption === EvidenceSearchOption.Address}
                                      onChange={(e) => handleSearchOptionChange(e.target.value as EvidenceSearchOption)}
                                      id={`accuracy-evidence-viewer__search-option-radio-address`}
                                      value="Address"
                                      testId="accuracy-evidence__search-criteria--address"
                                    />
                                  </QESRadioGroup>
                                </div>
                                {searchOption === EvidenceSearchOption.Npi &&
                                  <input className="form-control mb-2" placeholder="NPI" value={npi} onKeyUp={handleKeyUp} onChange={handleSearchCriteriaChange} name="npi" />}
                                {searchOption === EvidenceSearchOption.Xid &&
                                  <input className="form-control mb-2" placeholder="Xid" value={xid} onKeyUp={handleKeyUp} onChange={handleSearchCriteriaChange} name="xid" />}
                                {searchOption === EvidenceSearchOption.Address &&
                                  <AddressSearchBar address={address} onKeyUp={handleKeyUp} onSearchChange={handleSearchCriteriaChange} onSelectChange={handleSearchCriteriaChangeSelect} />}
                              </div>
                              <div className="col-sm mt-auto mb-2">
                                <QESDateTimeInput
                                  label="As of"
                                  testId="accuracy-evidence__as-of-date"
                                  value={asOfDate ? formatDateTimeLocal(asOfDate) : ''}
                                  min={formatDateTimeLocal(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000))}
                                  max={formatDateTimeLocal(new Date())}
                                  onChange={(e) => {
                                    setAsOfDate(e.target.value ? new Date(e.target.value) : null);
                                    setShowDateOutOfRangeWarning(false);
                                  }}
                                />
                              </div>
                              <div className="col-auto mt-auto mb-2">
                                <QESButton
                                  qesTestId="accuracy-evidence_search"
                                  qesType="primary"
                                  className="float-end"
                                  disabled={!searchEnabled}
                                  onClick={handleSubmit}>
                                  <QesIcon iconName={QesIcons.SearchSm} isDecorative className="mb3" /> Search
                                </QESButton>
                              </div>
                              <div className="col-auto mt-auto mb-2">
                                <QESButton
                                  qesTestId="accuracy-evidence_clear"
                                  qesType="primary"
                                  className="float-end"
                                  onClick={handleClear}>Clear
                                </QESButton>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {showDateOutOfRangeWarning &&
                      <div className="row mt-2">
                        <div className="col-xl-12 col-lg-10">
                          <div className="alert alert-warning alert-dismissible" role="alert">
                            The project run date is more than 365 days ago and is outside the allowed range. Please manually select a new &ldquo;As of&rdquo; date.
                            <button type="button" className="close" onClick={() => setShowDateOutOfRangeWarning(false)}>
                              <span aria-hidden="true">&times;</span>
                            </button>
                          </div>
                        </div>
                      </div>}
                    {isLoading &&
                      <div style={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
                        <LoadingSpinner />
                      </div>}
                    {hasSearched && !isLoading &&
                      <div className="row">
                        <div className="col-xl-12 col-lg-10">
                          <div className="card card-expansion-item card-static">
                            <div className="card-body">
                              <div className="tabs-style-2">
                                <ul className="nav nav-tabs">
                                  {(!isEmpty(evidenceTabular)) && Object.keys(evidenceTabular).map((evidenceType) => (
                                    <li className="nav-item" key={evidenceType}>
                                      <a role="tab" data-bs-toggle="tab" className={evidenceType === selectedEvidenceType ? "nav-link active" : "nav-link"} onClick={() => setSelectedEvidenceType(evidenceType)}>{getEvidenceTypeDisplayName(evidenceType)}</a>
                                    </li>
                                  ))}
                                </ul>
                                <div className="tab-content">
                                  <div className="tab-pane-active">
                                    {getEvidenceTab()}
                                  </div>
                                </ div>
                              </div>
                              {(selectedEvidenceType !== "QDP" && selectedEvidenceType !== "ProviderClaimsActivity") ? showNonQdpExpandButton() : showQdpExpandButton()}
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  </PageSection>
                </PageInner>
              </Page>
            </ErrorBoundary>)
          : (<Unauthorized />)
      )}
    </UserContext.Consumer>);
}
